import { getAttributesString, getAttributesStringExcluding, isSelfClosingTag, wrapWithOuterWrappers, extractOriginalElementInfo, wrapWithOriginalElement, buildWrapperTag, hideMathAnnotations } from "../htmlUtils";
import { normalizeAnswerSections } from "util/topicStructure";
/**
 * 將 SynopsisWindow 的 HTML 按行數分頁
 * @param {string} htmlString - 原始 HTML 字串
 * @param {number} lineHeight - 行高（像素）
 * @param {number} pageSize - 每頁最大行數
 * @param {HTMLElement} referenceContainer - 用於獲取樣式的參考容器
 * @returns {{htmlString: string, totalPages: number}} 分頁後的 HTML 字串和總頁數
 */
export const splitSynopsisIntoPages_Topic108_2 = (htmlString, lineHeight, pageSize, referenceContainer) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    // 先把巢狀在 #question 內的 #answer / #analyze 搬成兄弟節點，
    // 讓切法只由內容決定、不由內容檔漏寫 </div> 決定（見 util/topicStructure）。
    normalizeAnswerSections(doc);
    
    // 提取外層包裝元素（從 body 的直接子節點開始，向上查找所有包裝元素）
    const outerWrappers = extractOuterWrappers(doc);
    
    const measureContainer = createMeasureContainer(referenceContainer, outerWrappers);
    
    // 直接找到 ExtendedContentContainer 元素（id="exContainer"）
    // 這樣 measureContainer 就能自然繼承 CSS module 的樣式（包括 br { display: none; }）
    const extendedContentContainer = document.getElementById('exContainer');
    if (extendedContentContainer) {
      extendedContentContainer.appendChild(measureContainer);
    } else {
      // 如果找不到 ExtendedContentContainer，則 fallback 到 document.body
      document.body.appendChild(measureContainer);
    }
  
    try {
      
      // 解析所有「新分頁區塊」標記，將內容分組
      const pageBlocks = parsePageBlocks(doc);
      
      const pages = [];
      
      pageBlocks.forEach((block) => {
        // 檢查原始 block.element 是否已經有對應的 section id
        const alreadyHasSectionId = hasSectionId(block.element, block.sectionId);
        
        // 圖片永遠自己佔一頁
        if (block.type === 'img') {
          const pageDiv = doc.createElement('div');
          pageDiv.className = 'synopsis-page';
          
          // 如果有原始元素信息，用原始元素的結構包裹
          let elementToAppend = wrapWithOriginalElement(
            block.element.cloneNode(true),
            block.originalElementInfo,
            doc
          );
          
          // 檢查 wrapWithOriginalElement 返回的元素是否已經有對應的 section id
          const wrappedContentHasSectionId = block.sectionId && 
            elementToAppend.nodeType === Node.ELEMENT_NODE && 
            elementToAppend.id === block.sectionId;
          
          // 如果原始元素沒有 section id，且 wrapWithOriginalElement 返回的元素也沒有 section id
          // 則需要用對應的 id 包裹
          if (block.sectionId && !alreadyHasSectionId && !wrappedContentHasSectionId) {
            const sectionWrapper = doc.createElement('div');
            sectionWrapper.id = block.sectionId;
            sectionWrapper.appendChild(elementToAppend);
            elementToAppend = sectionWrapper;
          }
          
          pageDiv.appendChild(elementToAppend);
          pages.push(pageDiv);
          return;
        }
        
        // 文字區塊：需要根據高度來分頁
        const textContent = collectTextNodesFromBlock(block.element);
        
        // 檢查是否會分頁（如果只有一頁，且原始元素已經有 section id，則不需要包裹）
        const blockPages = splitBlockIntoPages(
          textContent,
          pageSize,
          measureContainer,
          lineHeight,
          block.element,
          block.originalElementInfo,
          block.sectionId
        );
  
        
        // 判斷是否需要 section 包裹：
        // 1. 如果原始元素沒有 section id，需要加上
        // 2. 如果原始元素有 section id，但被分頁了（多於一頁），每一頁都需要加上（因為原本的 div 被切開了）
        // 3. 如果原始元素有 section id，且只有一頁，不需要再加（因為原始元素本身就有）
        const isSplit = blockPages.length > 1; // 是否被分頁
        const needsSectionWrapper = !alreadyHasSectionId || isSplit;
        
        // 檢查是否有需要包裹的子元素（如 .ans, .title 等）
        const wrappableChildElements = block.wrappableChildElements || [];
        
        blockPages.forEach((page, pageIndex) => {
          const pageDiv = doc.createElement('div');
          pageDiv.className = 'synopsis-page';
          
          const pageContent = page.html;
          
          // 檢查 pageContent 字符串中是否已經包含 section id
          // （可能因為 originalElementInfo 中有 id）
          const pageContentHasSectionId = block.sectionId && 
            (pageContent.includes(`id="${block.sectionId}"`) || 
             pageContent.includes(`id='${block.sectionId}'`));
          
          // 如果有原始元素信息，用原始元素的結構包裹
          // 如果 originalElementInfo 的 id 與 sectionId 相同，且需要 section wrapper，則移除 id
          // （因為 section wrapper 會負責添加 id）
          // 如果不需要 section wrapper（原始元素有 id 且只有一頁），則保留 id
          let elementInfoForWrapper = block.originalElementInfo;
          if (needsSectionWrapper && elementInfoForWrapper && elementInfoForWrapper.id === block.sectionId) {
            elementInfoForWrapper = {
              ...elementInfoForWrapper,
              id: '' // 移除 id，讓 section wrapper 來添加
            };
          }
          
          let wrappedContent = wrapWithOriginalElement(
            pageContent,
            elementInfoForWrapper,
            doc
          );
          
          // 如果需要子元素包裹（當區塊被分頁時）
          // 使用 wrappableChildElements 中文字最長的那個（通常是 .ans）
          if (isSplit && wrappableChildElements.length > 0) {
            // 找到文字最長的子元素（通常是最可能被分頁的元素，如 .ans）
            let longestChildElement = wrappableChildElements[0];
            let maxTextLength = 0;
            
            // 從原始 block.element 中獲取實際的文字長度
            const originalBlockClone = block.element.cloneNode(true);
            
            wrappableChildElements.forEach(childInfo => {
              const childClassName = childInfo.className ? childInfo.className.split(' ')[0] : '';
              if (childClassName) {
                const childEl = originalBlockClone.querySelector(`.${childClassName}`);
                if (childEl) {
                  const textLength = childEl.textContent.trim().length;
                  if (textLength > maxTextLength) {
                    maxTextLength = textLength;
                    longestChildElement = childInfo;
                  }
                }
              }
            });
            
            const childClassName = longestChildElement.className ? longestChildElement.className.split(' ')[0] : '';
            
            // 檢查 wrappedContent 內部是否已經有該子元素的包裹
            const hasChildElementInside = childClassName && 
              wrappedContent.nodeType === Node.ELEMENT_NODE &&
              wrappedContent.querySelector && 
              wrappedContent.querySelector(`.${childClassName}`);
            
            // 檢查 wrappedContent 本身是否就是該子元素
            const isChildElement = childClassName &&
              wrappedContent.nodeType === Node.ELEMENT_NODE &&
              wrappedContent.classList &&
              wrappedContent.classList.contains(childClassName);
            
            // 如果內容中沒有該子元素，則需要用子元素包裹
            if (!hasChildElementInside && !isChildElement) {
              const childWrapper = doc.createElement(longestChildElement.tagName);
              if (longestChildElement.className) {
                childWrapper.className = longestChildElement.className;
              }
              if (longestChildElement.id) {
                childWrapper.id = longestChildElement.id;
              }
              
              // 設置其他屬性
              if (longestChildElement.attributes) {
                const parser = new DOMParser();
                const tempDoc = parser.parseFromString(`<div ${longestChildElement.attributes}></div>`, 'text/html');
                const tempEl = tempDoc.body.firstElementChild;
                if (tempEl) {
                  Array.from(tempEl.attributes).forEach(attr => {
                    if (attr.name !== 'class' && attr.name !== 'id') {
                      childWrapper.setAttribute(attr.name, attr.value);
                    }
                  });
                }
              }
              
              childWrapper.appendChild(wrappedContent);
              wrappedContent = childWrapper;
            }
          }
          
          // 檢查 wrapWithOriginalElement 返回的元素是否已經有對應的 section id
          // （如果 originalElementInfo.id === block.sectionId，wrapWithOriginalElement 會創建有該 id 的元素）
          const wrappedContentHasSectionId = block.sectionId && 
            wrappedContent.nodeType === Node.ELEMENT_NODE && 
            wrappedContent.id === block.sectionId;
          
          // 如果需要 sectionId 包裹，且這一頁的內容中沒有 section id
          // 且 wrapWithOriginalElement 返回的元素也沒有 section id
          if (block.sectionId && needsSectionWrapper && !pageContentHasSectionId && !wrappedContentHasSectionId) {
            const sectionWrapper = doc.createElement('div');
            sectionWrapper.id = block.sectionId;
            sectionWrapper.appendChild(wrappedContent);
            wrappedContent = sectionWrapper;
          }
          
          // NBK-1665：把區塊前置節點（div.img 標籤圖）還原成文字容器的前置兄弟，
          // 而不是被包在 .abreast_subject 內。只放在該區塊的第一頁；若已建立
          // section wrapper（#question），則放進 section 內，保持與原始 HTML 一致的
          // 「#question > .img + .abreast_subject」層級。
          if (block.leadingHtml && pageIndex === 0) {
            const leadingHolder = doc.createElement('div');
            leadingHolder.innerHTML = block.leadingHtml;
            const leadingNodes = Array.from(leadingHolder.childNodes);
            const sectionRoot =
              wrappedContent.nodeType === Node.ELEMENT_NODE &&
              wrappedContent.id === block.sectionId
                ? wrappedContent
                : null;

            if (sectionRoot) {
              // 反向插入到最前面，維持原本的節點順序
              leadingNodes.reverse().forEach(node => {
                sectionRoot.insertBefore(node, sectionRoot.firstChild);
              });
            } else {
              leadingNodes.forEach(node => {
                pageDiv.appendChild(node);
              });
            }
          }

          pageDiv.appendChild(wrappedContent);
          pages.push(pageDiv);
        });
      });
  
      // 創建一個臨時容器來收集所有頁面元素
      const container = doc.createElement('div');
      pages.forEach(page => {
        container.appendChild(page);
      });
      
      // 用外層包裝元素包裹所有頁面
      const finalHTML = wrapWithOuterWrappers(container.innerHTML, outerWrappers, doc);
      
      return {
        htmlString: finalHTML,
        totalPages: pages.length || 1,
      };
    } finally {
      // 從父容器中移除 measureContainer（可能是 ExtendedContentContainer 或 document.body）
      if (measureContainer.parentNode) {
        measureContainer.parentNode.removeChild(measureContainer);
      }
    }
  };
  
  /**
   * 提取外層包裝元素（從 body 的直接子節點開始，向上查找所有包裝元素）
   * 例如：<p class='MsoNormal'><div class='topic'>...</div></p>
   * @param {Document} doc - DOMParser 解析後的文檔
   * @returns {Array<{tagName: string, className: string, id: string, attributes: string}>}
   */
  const extractOuterWrappers = (doc) => {
    const wrappers = [];
    const body = doc.body;
    
    // 從 body 的直接子節點開始，向上查找所有包裝元素
    // 找到 question、answer 或 analyze 元素，然後向上查找所有父元素
    const questionEl = body.querySelector('#question');
    const answerEl = body.querySelector('#answer');
    const analyzeEl = body.querySelector('#analyze');
    
    // 使用第一個找到的元素作為參考點
    const referenceEl = questionEl || answerEl || analyzeEl;
    
    if (referenceEl) {
      // 向上查找所有父元素，直到 body
      let current = referenceEl.parentElement;
      while (current && current !== body && current !== doc.documentElement) {
        // 提取元素信息
        const wrapperInfo = {
          tagName: current.tagName.toLowerCase(),
          className: current.className || '',
          id: current.id || '',
          attributes: getAttributesStringExcluding(current, ['class', 'id'])
        };
        
        // 只添加有 className 或 id 的元素，或者是常見的包裝元素（p, div）
        if (wrapperInfo.className || wrapperInfo.id || 
            ['p', 'div', 'section', 'article'].includes(wrapperInfo.tagName)) {
          wrappers.unshift(wrapperInfo); // 從外層到內層的順序
        }
        
        current = current.parentElement;
      }
    }
    
    return wrappers;
  };
  
  
  /**
   * 解析 HTML 文檔，根據實際結構識別分頁區塊
   * 規則：
   * 1. .question 之下第一個 .img 和他下面內含文字的 div 是一個區塊
   * 2. 之後只要遇到 .img 都切分成一個新區塊（圖片自己佔一頁）
   * 3. 之後的每個 div 和 br 直到 .answer 都是一個區塊
   * 4. 剩下的 .analyze 也是一個區塊
   * @param {Document} doc - DOMParser 解析後的文檔
   * @returns {Array<{type: 'text'|'img', element: Element}>}
   */
  const parsePageBlocks = (doc) => {
    const blocks = [];
    const body = doc.body;
    
    // 找到 question element（可能是 #question 或 .question）
    const questionEl = body.querySelector('#question');
    
    // 處理 question 區塊：第一個 .img 和它下面的第一個內含文字的 div
    // 需要完整保留它們之間的所有節點（包括 <br> 和文字節點）
    const firstImg = questionEl.querySelector('.img');
    if (firstImg) {
      // 找到第一個 img 的起始位置
      const questionNodes = Array.from(questionEl.childNodes);
      const firstImgIndex = questionNodes.findIndex(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        return node === firstImg || (node.contains && node.contains(firstImg));
      });
      
      // 找到第一個文字 div（在第一個 img 之後）
      let firstTextDivEndIndex = -1;
      
      for (let i = firstImgIndex; i < questionNodes.length; i++) {
        const node = questionNodes[i];
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        
        // 如果遇到下一個 img（不是第一個 img），停止
        if (i > firstImgIndex) {
          const isImgContainer = (node.classList && node.classList.contains('img')) || node.tagName === 'IMG';
          if (isImgContainer && node !== firstImg && firstImg.contains && !firstImg.contains(node)) {
            break;
          }
        }
        
        // 找到第一個包含文字的 div
        if (node.tagName === 'DIV' && node.textContent.trim() && node !== firstImg) {
          firstTextDivEndIndex = i;
          break;
        }
      }
      
      if (firstTextDivEndIndex >= firstImgIndex) {
        // 收集從第一個 img 開始到第一個文字 div 結束的所有節點
        const questionBlockNodes = [];
        // NBK-1665：文字 div 之前的節點（例如 div.img 的標籤圖）在原始 HTML 中是
        // 文字容器的「兄弟」，不能跟著一起被文字 div 的 class 包起來。
        // 下方 originalElementInfo 取的是第一個文字 div（通常是 .abreast_subject），
        // 若把 .img 也放進同一個 container，組頁時整包會被再包一層 .abreast_subject，
        // 使 .img 變成它的子元素，於是 topic108-2.css 的
        //   .topic #question .abreast_subject img { max-width: 40%; float: right; }
        // 誤中，標籤圖從左上角被推到右上角。
        // 因此改為分開收集，由組頁端還原成 wrapper 的前置兄弟。
        const leadingNodes = [];
        let firstTextDivElement = null;
        for (let i = firstImgIndex; i <= firstTextDivEndIndex; i++) {
          const node = questionNodes[i];
          // 找到第一個文字 div 的原始元素（用於保存 className/id）
          if (!firstTextDivElement && node.nodeType === Node.ELEMENT_NODE &&
              node.tagName === 'DIV' && node.textContent.trim() && node !== firstImg) {
            firstTextDivElement = node;
          }
          if (firstTextDivElement) {
            questionBlockNodes.push(node.cloneNode(true));
          } else {
            leadingNodes.push(node.cloneNode(true));
          }
        }

        // 創建容器並添加所有節點（保留完整結構）
        const container = doc.createElement('div');
        questionBlockNodes.forEach(node => {
          container.appendChild(node);
        });

        // 提取原始元素信息（優先使用第一個文字 div，否則使用 questionEl）
        const originalElementInfo = extractOriginalElementInfo(firstTextDivElement || questionEl);

        // 前置節點以 HTML 字串保存，交由組頁端放回 wrapper 之外
        const leadingHolder = doc.createElement('div');
        leadingNodes.forEach(node => {
          leadingHolder.appendChild(node);
        });

        blocks.push({
          type: 'text',
          element: container,
          originalElementInfo: originalElementInfo,
          leadingHtml: leadingHolder.innerHTML,
          sectionId: 'question' // 標記為 question 區塊
        });
      } else {
        // 如果沒找到文字 div，只包含第一個 img
        const originalElementInfo = extractOriginalElementInfo(firstImg);
        blocks.push({
          type: 'text',
          element: firstImg.cloneNode(true),
          originalElementInfo: originalElementInfo,
          sectionId: 'question' // 標記為 question 區塊
        });
      }
    } else {
      // 如果沒有 img，將整個 question 作為一個區塊
      const originalElementInfo = extractOriginalElementInfo(questionEl);
      blocks.push({
        type: 'text',
        element: questionEl.cloneNode(true),
        originalElementInfo: originalElementInfo,
        sectionId: 'question' // 標記為 question 區塊
      });
    }
    
    // 處理 question 之後到 answer 之間的所有元素
    const answerEl = body.querySelector('#answer');
    const analyzeEl = body.querySelector('#analyze');
    
    // 遍歷 body 的所有直接子節點，收集 question 和 answer 之間的元素
    // 需要包括 question 內部的其他元素（除了第一個 img 和第一個文字 div）
    const bodyChildren = Array.from(body.childNodes);
    const elementsBetweenQuestionAndAnswer = [];
    let questionFound = false;
    
    // 先收集 question 內部除了第一個 img 和第一個文字 div 之外的所有節點
    // 需要找到第一個文字 div 結束的位置，只收集它之後的所有節點（包括文字節點和 <br>）
    const questionNodes = Array.from(questionEl.childNodes);
    let firstImgFound = false;
    let firstTextDivFound = false;
    let firstTextDivEndIndex = -1;
    
    // 先找出第一個 img 和第一個文字 div 的位置
    for (let i = 0; i < questionNodes.length; i++) {
      const node = questionNodes[i];
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      
      const element = node;
      
      // 找到第一個 img
      if (!firstImgFound && ((element.classList && element.classList.contains('img')) || element.tagName === 'IMG')) {
        firstImgFound = true;
        continue;
      }
      
      // 找到第一個包含文字的 div（在第一個 img 之後）
      if (firstImgFound && !firstTextDivFound && element.tagName === 'DIV' && element.textContent.trim()) {
        firstTextDivFound = true;
        firstTextDivEndIndex = i;
        break;
      }
    }
    
    // 只收集第一個文字 div 之後的所有節點（包括文字節點、<br> 等）
    if (firstTextDivEndIndex >= 0) {
      for (let i = firstTextDivEndIndex + 1; i < questionNodes.length; i++) {
        const node = questionNodes[i];
        elementsBetweenQuestionAndAnswer.push(node.cloneNode(true));
      }
    }
    
    // 然後收集 question 之後到 answer 之間的元素
    for (const node of bodyChildren) {
      // 跳過非元素節點
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      
      const element = node;
      
      // 遇到 question 元素後開始收集
      if (element === questionEl) {
        questionFound = true;
        continue;
      }
      
      // 如果還沒遇到 question，跳過
      if (!questionFound) continue;
      
      // 如果遇到 answer 或 analyze，停止收集
      if (element === answerEl || element === analyzeEl) {
        break;
      }
      
      elementsBetweenQuestionAndAnswer.push(element);
    }
    
    // 處理 question 和 answer 之間的元素
    // 規則：
    // 1. 每個 img 單獨成為一個區塊（圖片自己佔一頁）
    // 2. 所有含有文字的 div 和 br 合併成一個區塊
    // 提取所有 img 元素和文字元素
    // 如果圖片或文字節點後面跟著 <br>，<br> 應該跟著一起收集
    const extractImgsAndTextElements = (nodes) => {
      const imgContainers = [];
      const textElements = [];
      const processedIndices = new Set();
      
      // 跳過開頭的單獨 <br> 節點（因為 <br> 應該跟著前一個元素）
      let startIndex = 0;
      while (startIndex < nodes.length) {
        const node = nodes[startIndex];
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
          startIndex++;
        } else {
          break;
        }
      }
      
      // 按順序遍歷所有節點（從第一個非 <br> 節點開始）
      for (let i = startIndex; i < nodes.length; i++) {
        if (processedIndices.has(i)) continue;
        
        const node = nodes[i];
        
        // 處理文字節點：檢查後方是否有 <br>
        if (node.nodeType === Node.TEXT_NODE) {
          const textBlock = [node.cloneNode(true)];
          
          // 檢查下一個節點是否是 <br>
          if (i + 1 < nodes.length) {
            const nextNode = nodes[i + 1];
            if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
              textBlock.push(nextNode.cloneNode(true));
              processedIndices.add(i + 1);
            }
          }
          
          // 文字節點需要包裹在容器中
          const container = doc.createElement('div');
          textBlock.forEach(n => container.appendChild(n));
          textElements.push(container);
          
          processedIndices.add(i);
          continue;
        }
        
        // 處理非元素節點
        if (node.nodeType !== Node.ELEMENT_NODE) {
          textElements.push(node.cloneNode(true));
          processedIndices.add(i);
          continue;
        }
        
        const element = node;
        
        // 如果元素本身是 .img 容器，收集它和它後面可能跟著的 <br>
        // 圖片結構確定是 <div class="img"><br><img><br></div><br>
        if (element.classList && element.classList.contains('img')) {
          // 如果後面有 <br>，需要包裹；否則直接使用 .img 元素本身
          if (i + 1 < nodes.length) {
            const nextNode = nodes[i + 1];
            if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
              // 有 <br>，需要包裹
              const container = doc.createElement('div');
              container.appendChild(element.cloneNode(true));
              container.appendChild(nextNode.cloneNode(true));
              imgContainers.push(container);
              processedIndices.add(i + 1);
            } else {
              // 沒有 <br>，直接使用 .img 元素
              imgContainers.push(element.cloneNode(true));
            }
          } else {
            // 沒有下一個節點，直接使用 .img 元素
            imgContainers.push(element.cloneNode(true));
          }
          
          processedIndices.add(i);
          continue;
        }
        
        // 其他元素（包括 <br>、文字 div 等）
        // 如果遇到單獨的 <br>，跳過它（因為 <br> 應該跟著前一個元素）
        if (element.tagName === 'BR') {
          processedIndices.add(i);
          continue;
        }
        
        // 檢查後方是否有 <br>
        if (i + 1 < nodes.length) {
          const nextNode = nodes[i + 1];
          if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
            // 有 <br>，需要包裹
            const container = doc.createElement('div');
            container.appendChild(element.cloneNode(true));
            container.appendChild(nextNode.cloneNode(true));
            textElements.push(container);
            processedIndices.add(i + 1);
          } else {
            // 沒有 <br>，直接使用元素本身（如果已經是完整的 HTML 單元）
            // 對於 div 等容器元素，直接使用；對於 br 等單個標籤，也直接使用
            textElements.push(element.cloneNode(true));
          }
        } else {
          // 沒有下一個節點，直接使用元素本身
          textElements.push(element.cloneNode(true));
        }
        
        processedIndices.add(i);
      }
      
      return { imgContainers, textElements };
    };
    
    const { imgContainers, textElements } = extractImgsAndTextElements(elementsBetweenQuestionAndAnswer);
    
    // 先處理所有 img，每個 img 單獨成為一個區塊
    // 這些圖片在 question 和 answer 之間，通常屬於 question 區域
    imgContainers.forEach((imgContainer) => {
      // 從 imgContainer 中提取原始元素信息
      let originalElementInfo = null;
      if (imgContainer.nodeType === Node.ELEMENT_NODE) {
        // 查找容器中的 .img 元素
        const imgEl = imgContainer.querySelector('.img') || imgContainer;
        originalElementInfo = extractOriginalElementInfo(imgEl);
      }
      blocks.push({
        type: 'img',
        element: imgContainer.cloneNode(true),
        originalElementInfo: originalElementInfo,
        sectionId: 'question' // 圖片通常屬於 question 區域
      });
    });
    
    // 處理所有文字元素，合併成一個區塊
    // 這些元素在 question 和 answer 之間，屬於 question 區域
    if (textElements.length > 0) {
      // 從 textElements 中找到有 className 或 id 的元素作為原始元素
      let originalElementInfo = null;
      for (const el of textElements) {
        if (el.nodeType === Node.ELEMENT_NODE) {
          const info = extractOriginalElementInfo(el);
          if (info && (info.className || info.id)) {
            originalElementInfo = info;
            break;
          }
        }
      }
      
      blocks.push({
        type: 'text',
        element: wrapElements(textElements, doc),
        originalElementInfo: originalElementInfo,
        sectionId: 'question' // 標記為 question 區塊
      });
    }
    
    // NBK-1666：部分書本的 #answer／#analyze 是巢狀在 #question 內部（英文題常見；
    // 原始 HTML 的 .topic／#question 標籤未閉合，DOMParser 補完後答案會落在 #question
    // 底下）。此時上方的 question 區塊已經整份複製了 #question（連同答案），若再送出
    // 獨立的 answer 區塊，同一個答案會被輸出兩次：
    //   - 多出一頁「只有答案」的頁面
    //   - 兩頁都帶 id="answer"，而右下角按鈕用的是 document.getElementById('answer')，
    //     只會取到文件中第一個，於是在第二頁按下去毫無反應
    //   - 不同書本結構不同（巢狀 vs 兄弟），答案落在第幾頁也就不一致
    // 兄弟結構（#question 與 #answer 平行，數學題常見）不受影響，答案照舊獨立成頁。
    const answerNestedInQuestion = !!(answerEl && questionEl && questionEl.contains(answerEl));
    const analyzeNestedInQuestion = !!(analyzeEl && questionEl && questionEl.contains(analyzeEl));

    // 處理 answer 區塊（如果存在且不在 analyze 之前）
    if (answerEl && answerEl !== analyzeEl && !answerNestedInQuestion) {
      const originalElementInfo = extractOriginalElementInfo(answerEl);
      const wrappableChildElements = extractWrappableChildElements(answerEl);
      blocks.push({
        type: 'text',
        element: answerEl.cloneNode(true),
        originalElementInfo: originalElementInfo,
        wrappableChildElements: wrappableChildElements,
        sectionId: 'answer' // 標記為 answer 區塊
      });
    }
    
    // 處理 analyze 區塊
    if (analyzeEl && !analyzeNestedInQuestion) {
      const originalElementInfo = extractOriginalElementInfo(analyzeEl);
      const wrappableChildElements = extractWrappableChildElements(analyzeEl);
      blocks.push({
        type: 'text',
        element: analyzeEl.cloneNode(true),
        originalElementInfo: originalElementInfo,
        wrappableChildElements: wrappableChildElements,
        sectionId: 'analyze' // 標記為 analyze 區塊
      });
    }
    
    return blocks;
  };
  
  /**
   * 將多個元素包裝在一個容器中
   * @param {Array<Element>} elements - 要包裝的元素陣列
   * @param {Document} doc - 文檔對象，用於創建新元素
   */
  const wrapElements = (elements, doc) => {
    // 如果只有一個元素，直接返回它（不需要額外包裹）
    if (elements.length === 1) {
      const el = elements[0];
      return el.ownerDocument === doc ? el : el.cloneNode(true);
    }
    
    // 多個元素需要包裹在容器中
    const container = doc.createElement('div');
    elements.forEach(el => {
      // 如果元素已經是 cloneNode 的結果，直接使用；否則需要 clone
      const elementToAppend = el.ownerDocument === doc ? el : el.cloneNode(true);
      container.appendChild(elementToAppend);
    });
    return container;
  };
  
  /**
   * 創建用於測量的臨時容器
   * @param {HTMLElement} referenceContainer - 參考容器
   * @param {Array} outerWrappers - 外層包裝元素信息（包含 .topic 等）
   */
  const createMeasureContainer = (referenceContainer, outerWrappers) => {
    const measureContainer = document.createElement('div');
    const containerStyle = window.getComputedStyle(referenceContainer);
    
    measureContainer.style.position = 'absolute';
    measureContainer.style.visibility = 'hidden';
    measureContainer.style.width = referenceContainer.offsetWidth + 'px';
    measureContainer.style.fontSize = containerStyle.fontSize;
    measureContainer.style.lineHeight = containerStyle.lineHeight;
    measureContainer.style.fontFamily = containerStyle.fontFamily;
    
    // 創建外層包裝元素的 DOM 結構（如 .topic），確保 CSS 選擇器能正確匹配
    // 注意：br { display: none; } 等樣式會從父容器（ExtendedContentContainer）自然繼承
    if (outerWrappers && outerWrappers.length > 0) {
      let currentContainer = measureContainer;
      
      // 從外層到內層依次創建包裝元素
      outerWrappers.forEach((wrapper, index) => {
        const wrapperEl = document.createElement(wrapper.tagName);
        if (wrapper.className) {
          wrapperEl.className = wrapper.className;
        }
        if (wrapper.id) {
          wrapperEl.id = wrapper.id;
        }
        
        currentContainer.appendChild(wrapperEl);
        currentContainer = wrapperEl;
      });
      
      // 創建一個內部容器用於放置測量內容
      const innerContainer = document.createElement('div');
      currentContainer.appendChild(innerContainer);
      measureContainer._innerContainer = innerContainer;

      // 攔截 innerHTML setter，每次寫入後自動移除 MathML annotation
      const originalDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
      Object.defineProperty(innerContainer, 'innerHTML', {
        get() { return originalDescriptor.get.call(this); },
        set(html) {
          originalDescriptor.set.call(this, html);
          hideMathAnnotations(this);
        },
        configurable: true,
      });
    }

    return measureContainer;
  };
  
  /**
   * 從區塊元素中收集所有文字和算式節點
   * 保留所有 HTML 結構（包括 <br>、div 等），以便正確測量高度和分頁
   */
  const collectTextNodesFromBlock = (blockElement) => {
    const nodes = [];
    
    const collect = (node, result) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text.trim()) {
          // 將文字按照英文單字和中文/其他字符分組
          // 英文單字：連續的字母、數字、連字符、撇號等
          // 中文和其他字符：逐字符處理
          const wordPattern = /[\w'-]+/g; // 匹配英文單字（包括字母、數字、連字符、撇號）
          let lastIndex = 0;
          let match;
          
          while ((match = wordPattern.exec(text)) !== null) {
            // 添加英文單字前的非英文字符（逐字符）
            if (match.index > lastIndex) {
              const nonWordText = text.substring(lastIndex, match.index);
              for (const char of nonWordText) {
                result.push({ 
                  type: 'text', 
                  content: char
                });
              }
            }
            
            // 添加英文單字作為整體
            result.push({ 
              type: 'text', 
              content: match[0]
            });
            
            lastIndex = match.index + match[0].length;
          }
          
          // 添加剩餘的非英文字符（逐字符）
          if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            for (const char of remainingText) {
              result.push({ 
                type: 'text', 
                content: char
              });
            }
          }
        } else if (text) {
          // 保留空白文字（包括換行符等）
          result.push({ 
            type: 'text', 
            content: text
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const isEquation = node.classList && node.classList.contains('MTConvertedEquation');
        // .img 元素也需要作為完整單元保留，不能拆分成字符
        const isImgContainer = node.classList && node.classList.contains('img');
        // 選項區：內含多個選擇題（多行），題目必須全部留在同一頁。
        // 整塊當一個單元，splitBlockIntoPages 加進目前這頁會超出行數限制時，就整塊換到下一頁。
        // 若照一般容器逐字拆，會在選項中間斷頁，而且下一頁從半截結構開始、
        // 缺了 <div class="option_box"> 開頭標籤，.option_box .option 的樣式也套不上。
        // .abreast_option 是「選項區 + 右側題圖」的並排外框（topic108-2.css），
        // 只把裡面的 .option_box 當單元的話，換頁時外框會被拆開，
        // 選項脫離 .abreast_option .option_box { width: 60% } 的版面、題圖也留在前一頁，所以整個外框一起。
        const isOptionBlock = node.classList &&
          (node.classList.contains('option_box') || node.classList.contains('abreast_option'));

        // 對於自閉合標籤（如 <br>），直接保留 outerHTML
        if (isSelfClosingTag(node.tagName)) {
          result.push({
            type: 'equation',
            content: node.outerHTML
          });
        }
        // 對於不可分割的單元（算式、圖片、選項區），保留完整的 HTML
        else if (isEquation || isImgContainer || isOptionBlock) {
          result.push({ 
            type: 'equation', 
            content: node.outerHTML
          });
        } 
        // 對於容器元素（div 等），保留標籤結構但內容需要拆分成字符
        else {
          // 添加開始標籤（包含所有屬性）
          const startTag = `<${node.tagName.toLowerCase()}${getAttributesString(node)}>`;
          result.push({ 
            type: 'equation', 
            content: startTag
          });
          
          // 遞迴處理子節點（文字會被拆分成字符）
          Array.from(node.childNodes).forEach(child => {
            collect(child, result);
          });
          
          // 添加結束標籤
          result.push({ 
            type: 'equation', 
            content: `</${node.tagName.toLowerCase()}>`
          });
        }
      }
    };
    
    Array.from(blockElement.childNodes).forEach(node => {
      collect(node, nodes);
    });
    
    return nodes;
  };
  
  
  
  
  
  /**
   * 檢查元素或其內部是否已經有對應的 section id
   * @param {Element|string} element - 要檢查的元素
   * @param {string} sectionId - section id（如 'question', 'answer', 'analyze'）
   * @returns {boolean}
   */
  const hasSectionId = (element, sectionId) => {
    if (!element || !sectionId) return false;
    
    // 如果是字符串，需要先解析
    if (typeof element === 'string') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${element}</div>`, 'text/html');
      element = doc.body.firstElementChild;
    }
    
    // 檢查元素本身
    if (element.nodeType === Node.ELEMENT_NODE && element.id === sectionId) {
      return true;
    }
    
    // 檢查子元素
    if (element.querySelector && element.querySelector(`#${sectionId}`)) {
      return true;
    }
    
    return false;
  };
  
  /**
   * 提取區塊中需要包裹的子元素信息（如 .ans, .title 等）
   * 這些子元素可能包含會被分頁的內容
   * @param {Element} blockElement - 區塊元素
   * @returns {Array<{tagName: string, className: string, id: string, attributes: string}>}
   */
  const extractWrappableChildElements = (blockElement) => {
    const wrappableElements = [];
    
    if (!blockElement || blockElement.nodeType !== Node.ELEMENT_NODE) {
      return wrappableElements;
    }
    
    // 遍歷直接子元素，找到有 className 的元素（如 .ans, .title, .year 等）
    Array.from(blockElement.children).forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE && child.className) {
        // 只提取有 className 且包含文字內容的子元素
        // 排除 .img, MTConvertedEquation 等不可分割的元素
        const isSpecialElement = child.classList.contains('img') || 
                                  child.classList.contains('MTConvertedEquation');
        
        if (!isSpecialElement && child.className.trim() && child.textContent.trim()) {
          const info = {
            tagName: child.tagName.toLowerCase(),
            className: child.className || '',
            id: child.id || '',
            attributes: getAttributesStringExcluding(child, ['class', 'id'])
          };
          wrappableElements.push(info);
        }
      }
    });
    
    return wrappableElements;
  };
  
  
  
  /**
   * 將節點陣列切分成多頁
   * 返回包含 HTML 的對象陣列
   */
  const splitBlockIntoPages = (nodes, pageSize, measureContainer, lineHeight, originalBlock, originalElementInfo, sectionId) => {
    const pages = [];
    let currentPageHTML = '';
    
    // 構建包裹標籤（用於測量）
    const { startTag, endTag } = buildWrapperTag(originalElementInfo);
    
    // 如果測量容器有 _innerContainer（包含外層 .topic 結構），將 HTML 放入內層容器
    const contentContainer = measureContainer._innerContainer || measureContainer;
    
    // 每一頁的最大高度（7 行）
    const maxHeight = pageSize * lineHeight;
    
    nodes.forEach((node) => {
      const nodeContent = node.content;
      
      if (!nodeContent) return;
  
      // 測量加入當前節點後的總行數（使用原始元素的結構包裹）
      // 如果有 sectionId，需要包含在測量中（確保 CSS 選擇器 .topic #question 等能正確匹配）
      const shouldWrapWithSectionId =
        sectionId &&
        (!originalElementInfo || originalElementInfo.id !== sectionId);
  
      let testHTML = startTag + currentPageHTML + nodeContent + endTag;
      if (shouldWrapWithSectionId) {
        testHTML = `<div id="${sectionId}">${testHTML}</div>`;
      }
      contentContainer.innerHTML = testHTML;
      // 測量 contentContainer（_innerContainer）的高度，而非 measureContainer
      // measureContainer 包含外層 .topic wrapper 的額外高度（padding/overflow 等），
      // 與 maxHeight（純內容行高 × 行數）的基準不一致
      const testHeight = contentContainer.offsetHeight;

      // 結束標籤與純空白不會增加可見內容，不能為它們換頁。
      // 單元本身就比整頁高時（例如整塊換頁的選項區），這一頁已經超出，
      // 後面接著的 </div> 若也觸發換頁，會多出一頁只有結束標籤的空白頁。
      const addsNoContent = /^<\//.test(nodeContent) || !nodeContent.trim();

      if (testHeight > maxHeight + 1 && currentPageHTML && !addsNoContent) {
        // 超過限制，切到新頁
        pages.push({
          html: currentPageHTML
        });
        currentPageHTML = nodeContent;
      } else {
        currentPageHTML += nodeContent;
      }
    });
    
    // 最後一頁
    if (currentPageHTML) {
      pages.push({
        html: currentPageHTML
      });
    }
    
    return pages.length > 0 ? pages : [{ html: '' }];
  };